import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component, HostListener,
    Input, OnDestroy,
    OnInit,
    ViewChild,
    ViewRef
} from '@angular/core';
import { Dataset } from '../../../model/dataset/dataset';
import { DatapointsService } from '../../../data-access-layer/datapoints/datapoints.service';
import { ClusteringService } from '../../clustering/clustering.service';
import { DatapointFilter } from '../../../model/datapoint/filter/datapoint-filter';
import { DatasetFieldType } from '../../../model/dataset/dataset-field-type';
import { Cluster } from '../../clustering/cluster';
import { DatapointProjection } from '../../../model/datapoint/projection/datapoint-projection';
import { ClusterDatapoint } from '../../clustering/cluster-datapoint';
import { ClusterType, Constants, DistanceUnit } from '../../../constants';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import { NotifService } from '../../../core/notification/notif.service';
import { GISUtils } from '../../../core/utils/gis-utils';
import { DatapointsPageStateService } from '../datapoints-page-state.service';
import { DatasetField } from '../../../model/dataset/field/dataset-field';
import { Sort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { DownloadService } from '../../../data-access-layer/download/download.service';
import { DownloadDatapointRequest } from '../../../model/download/download-datapoint-request';
import { AttachmentUtils } from '../../../core/utils/attachment-utils';
import { DownloadFileType } from '../../../model/download/download-file-type';
import { ClusteringRequest, ClusteringFieldType } from '../../clustering/clustering-request';
import { DataPointsServiceState } from '../../../shared/services/datapoints-service-state';
import { ObjectUtils } from 'src/app/core/utils/object-utils';
import { DatapointsFilterService } from '../datapoints-filter.service';
import {AuthService} from '../../../auth/auth.service';
import {PrincipalUser} from '../../../model/auth/principal-user';
import {DatasetFieldScope} from '../../../model/dataset/dataset-field-scope';
import {Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import { LayerExposeField } from 'src/app/account/layer-exposed/services/layer-expose-field';
import { LayerExposedService } from 'src/app/account/layer-exposed/services/layer-exposed.service';
import { WorkspaceItem } from 'src/app/model/workspace/workspace-item';

@Component({
    selector: 'map-datapoints-cluster-panel',
    templateUrl: './datapoints-cluster.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./datapoints-cluster.component.scss']
})
export class DatapointsClusterComponent implements OnInit, OnDestroy {

    private MAXIMUM_RADIUS = 10000;
    private MAXIMUM_RADIUS_FOR_FEET = 1000000000;
    private MAXIMUM_TOP_CLUSTERS = 1000000000;
    private MAX_DATAPOINTS_PER_CLUSTER = 20;
    filter: DatapointFilter;
    @Input() dataset: Dataset;
    @Input() initSettings: ClusteringRequest;
    @Input() layerExposeFields: LayerExposeField[] = [];
    @Input() formulas: WorkspaceItem[] = [];

    @ViewChild('table') table: MatTable<ClusterDatapoint>;

    datapointsColumns = ['summary', 'clusteringField', 'percentage'];
    clustersColumns = ['color', 'value', 'count', 'action'];

    distanceUnits = Object.keys(DistanceUnit);

    clusterType = Object.keys(ClusterType);

    clusterClone: Cluster;
    selectedCluster: Cluster;
    clusteringForm: UntypedFormGroup;
    clusteringField: DatasetField;
    clusters: Cluster[] = [];
    clusteringSettings: ClusteringRequest;
    
    // Field selection properties
    selectedFieldType: ClusteringFieldType = 'field';
    selectedFieldId: string = '';
    selectedFieldName: string = '';
    filterFieldSearchString: string = '';
    
    // Eligible numeric fields from dataset
    eligibleNumericFields: DatasetField[] = [];

    principal: PrincipalUser;
    private readonly subscriptions: Subscription = new Subscription();

    validChars = [
        'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', '.'
    ];

    constructor(private readonly clusteringService: ClusteringService,
        private readonly datapointsService: DatapointsService,
        private readonly datapointFilterService: DatapointsFilterService,
        private readonly dataPointsServiceState: DataPointsServiceState,
        private readonly datapointsPageStateService: DatapointsPageStateService,
        private readonly notifService: NotifService,
        private readonly authService: AuthService,
        private readonly downloadService: DownloadService,
        private readonly datapointsFilterService: DatapointsFilterService,
        private readonly layerExposeService: LayerExposedService,
        private readonly cd: ChangeDetectorRef) {
    }


    initClusteringForm() {
        this.clusteringForm = new UntypedFormGroup({
            radius: new UntypedFormControl(this.initSettings && this.initSettings.radius ?
                this.initSettings.radius :
                Constants.DEFAULT_RADIUS, [Validators.required, Validators.max(this.MAXIMUM_RADIUS), Validators.maxLength(7), this.radiusValidator.bind(this)]),
            fieldId: new UntypedFormControl(this.initSettings && this.initSettings.fieldId ?
                this.initSettings.fieldId :
                undefined, Validators.required),
            distanceUnit: new UntypedFormControl(this.initSettings && this.initSettings.distanceUnit ?
                this.initSettings.distanceUnit :
                DistanceUnit.KM, Validators.required),
            limit: new UntypedFormControl(this.initSettings && this.initSettings.limit ?
                this.initSettings.limit :
                Constants.DEFAULT_TOP_CLUSTERS, [Validators.max(this.MAXIMUM_TOP_CLUSTERS), this.forbiddenZeroFirst.bind(this)]),
            minCluster: new UntypedFormControl(this.initSettings && this.initSettings.minCluster ?
                this.initSettings.minCluster :
                null),
            maxCluster: new UntypedFormControl(this.initSettings && this.initSettings.maxCluster ?
                this.initSettings.maxCluster :
                null),
            type: new UntypedFormControl(this.initSettings && this.initSettings.type ?
                this.initSettings.type :
                ClusterType.TOP, Validators.required),    
        });
    }

    ngOnInit(): void {
        // initialize clustering panel form
        this.principal = this.authService.getPrincipal();
        this.initClusteringForm();
        this.initEligibleNumericFields();
        this.fetchLayerExposeFields();
        this.filter = this.datapointFilterService.getActiveFilter();
        this.subscriptions.add(
            this.datapointFilterService.onFilterChange().pipe(debounceTime(200)).subscribe(newFilter => {
                this.filter = newFilter;
                if (this.clusteringSettings) {
                    // clustering on filter change
                    // Use saved field type from settings (in case component properties are stale)
                    const fieldType = this.clusteringSettings.selectedFieldType || this.selectedFieldType;
                    const fieldId = this.clusteringSettings.selectedFieldId || this.selectedFieldId;
                    
                    // Build projection fields based on field type
                    const projectionFields: string[] = ['id', this.dataset.fields[this.dataset.mainSummaryFieldIndex].id];
                    if (fieldType === 'field') {
                        projectionFields.unshift(this.clusteringSettings.fieldId);
                    }
                    
                    const projection: any = {
                        datasetID: this.dataset.id,
                        fields: projectionFields,
                        linkFields: [],
                        links: [],
                        geometryPrecision: 25
                    };
                    
                    // Add formulas array if a formula is selected
                    if (fieldType === 'formula' && fieldId) {
                        const selectedFormula = this.formulas.find(f => f.id.toString() === fieldId);
                        if (selectedFormula) {
                            projection.formulas = [{
                                id: selectedFormula.id,
                                accountId: selectedFormula.accountId,
                                datasetId: selectedFormula.datasetId,
                                userId: this.principal.id, // Use authenticated user's ID
                                name: selectedFormula.name,
                                type: selectedFormula.type,
                                access: selectedFormula.access,
                                data: selectedFormula.data
                            }];
                            
                            // Extract field IDs from formula and add to projection
                            try {
                                const formulaData = JSON.parse(selectedFormula.data);
                                const formulaText = formulaData.formula || '';
                                const fieldMatches = formulaText.match(/FIELD_(\d+)/g);
                                if (fieldMatches) {
                                    fieldMatches.forEach(match => {
                                        const fId = match.replace('FIELD_', '');
                                        if (!projectionFields.includes(fId)) {
                                            projectionFields.push(fId);
                                        }
                                    });
                                    projection.fields = projectionFields;
                                }
                            } catch (e) {
                                console.error('Error parsing formula data:', e);
                            }
                        }
                    }
                    let radius = typeof this.clusteringSettings.radius === 'string' 
                        ? parseFloat(this.clusteringSettings.radius) 
                        : this.clusteringSettings.radius;
                    if (this.clusteringSettings.distanceUnit === DistanceUnit.MILES) {
                        radius = GISUtils.milesToKm(radius);
                    } else if (this.clusteringSettings.distanceUnit === DistanceUnit.FEET) {
                        radius = GISUtils.milesToFoot(radius);
                    } else if (this.clusteringSettings.distanceUnit === DistanceUnit.METER) {
                        radius = GISUtils.milesToMeter(radius);
                    }
                    let clonedFilter = ObjectUtils.clone(this.filter);
                    // if (this.principal.isSuperadmin) {
                    //     clonedFilter.groups = [];
                    // }
                    
                    // Build request based on field type
                    const request: any = {
                        datasetId: this.dataset.id,
                        filter: clonedFilter,
                        projection: projection,
                        fieldId: null,
                        layerExposedEquationId: null,
                        formulaId: null,
                        radius: radius,
                        limit: typeof this.clusteringSettings.limit === 'string' 
                            ? parseInt(this.clusteringSettings.limit, 10) 
                            : this.clusteringSettings.limit,
                        minCluster: this.clusteringSettings.minCluster || 0,
                        maxCluster: this.clusteringSettings.maxCluster || 0,
                        type: this.clusteringSettings.type
                    };
                    
                    // Set the appropriate field based on type
                    if (fieldType === 'field') {
                        request.fieldId = this.clusteringSettings.fieldId;
                    } else if (fieldType === 'layerExposeEquation') {
                        request.layerExposedEquationId = parseInt(fieldId, 10);
                    } else if (fieldType === 'formula') {
                        request.formulaId = parseInt(fieldId, 10);
                        // fieldId is still required even when using formula
                        request.fieldId = 'id';
                    }
                    
                    this.clusteringService.enableClusteringMode(request);}
            }));

        this.subscriptions.add(
            this.clusteringService.onClusteringActivated().subscribe(clusters => {
                this.clusters = clusters.clusters;
                if (this.cd && !(this.cd as ViewRef).destroyed) {
                    this.cd.detectChanges();
                }

                // Build projection fields based on field type
                const projectionFields: string[] = ['id', this.dataset.fields[this.dataset.mainSummaryFieldIndex].id];
                const fieldType = this.clusteringSettings.selectedFieldType || this.selectedFieldType;
                const fieldId = this.clusteringSettings.selectedFieldId || this.selectedFieldId;
                if (fieldType === 'field') {
                    projectionFields.unshift(this.clusteringSettings.fieldId);
                }
                
                const projection: any = {
                    datasetID: this.dataset.id,
                    fields: projectionFields,
                    linkFields: [],
                    links: [],
                    geometryPrecision: 25
                };
                
                // Add formulas array if a formula is selected
                if (fieldType === 'formula' && fieldId) {
                    const selectedFormula = this.formulas.find(f => f.id.toString() === fieldId);
                    if (selectedFormula) {
                        projection.formulas = [{
                            id: selectedFormula.id,
                            accountId: selectedFormula.accountId,
                            datasetId: selectedFormula.datasetId,
                            userId: this.principal.id, // Use authenticated user's ID
                            name: selectedFormula.name,
                            type: selectedFormula.type,
                            access: selectedFormula.access,
                            data: selectedFormula.data
                        }];
                        
                        // Extract field IDs from formula and add to projection
                        try {
                            const formulaData = JSON.parse(selectedFormula.data);
                            const formulaText = formulaData.formula || '';
                            const fieldMatches = formulaText.match(/FIELD_(\d+)/g);
                            if (fieldMatches) {
                                fieldMatches.forEach(match => {
                                    const fId = match.replace('FIELD_', '');
                                    if (!projectionFields.includes(fId)) {
                                        projectionFields.push(fId);
                                    }
                                });
                                projection.fields = projectionFields;
                            }
                        } catch (e) {
                            console.error('Error parsing formula data:', e);
                        }
                    }
                }
                let radius = typeof this.clusteringSettings.radius === 'string' 
                    ? parseFloat(this.clusteringSettings.radius) 
                    : this.clusteringSettings.radius;
                
                // Build settings based on field type
                const settings: any = {
                    filter: this.filter,
                    projection: projection,
                    radius: radius,
                    fieldId: null,
                    layerExposedEquationId: null,
                    formulaId: null,
                    datasetId: this.dataset.id,
                    limit: typeof this.clusteringSettings.limit === 'string' 
                        ? parseInt(this.clusteringSettings.limit, 10) 
                        : this.clusteringSettings.limit,
                    minCluster: this.clusteringSettings.minCluster || 0,
                    maxCluster: this.clusteringSettings.maxCluster || 0,
                    type: this.clusteringSettings.type
                };
                
                // Set the appropriate field based on type
                if (this.selectedFieldType === 'field') {
                    settings.fieldId = this.clusteringSettings.fieldId;
                } else if (this.selectedFieldType === 'layerExposeEquation') {
                    settings.layerExposedEquationId = parseInt(this.selectedFieldId, 10);
                } else if (this.selectedFieldType === 'formula') {
                    settings.formulaId = parseInt(this.selectedFieldId, 10);
                    settings.fieldId = 'id';
                }
                
                this.dataPointsServiceState.emitOnApplySettings(settings);
            }));

        this.subscriptions.add(
            this.clusteringService.onClusterSelected().subscribe(cluster => {
                cluster.datapoints.forEach(datapoint => {
                    let percentage = 100 * datapoint.value / cluster.value;
                    datapoint.percentage = percentage.toFixed(2);
                });

                this.clusterClone = ObjectUtils.clone(cluster);
                this.selectedCluster = ObjectUtils.clone(cluster);
                this.selectedCluster.datapoints = cluster.datapoints.slice(0, this.MAX_DATAPOINTS_PER_CLUSTER);
                if (this.cd && !(this.cd as ViewRef).destroyed) {
                    this.cd.detectChanges();
                }

                // Use saved field type from settings
                const fieldType = this.clusteringSettings.selectedFieldType || this.selectedFieldType;
                const fieldId = this.clusteringSettings.selectedFieldId || this.selectedFieldId;
                
                // Build projection fields based on field type
                const projectionFields: string[] = ['id', this.dataset.fields[this.dataset.mainSummaryFieldIndex].id];
                if (fieldType === 'field') {
                    projectionFields.unshift(this.clusteringSettings.fieldId);
                }
                
                const projection: any = {
                    datasetID: this.dataset.id,
                    fields: projectionFields,
                    linkFields: [],
                    links: [],
                    geometryPrecision: 25
                };
                
                // Add formulas array if a formula is selected
                if (fieldType === 'formula' && fieldId) {
                    const selectedFormula = this.formulas.find(f => f.id.toString() === fieldId);
                    if (selectedFormula) {
                        projection.formulas = [{
                            id: selectedFormula.id,
                            accountId: selectedFormula.accountId,
                            datasetId: selectedFormula.datasetId,
                            userId: this.principal.id, // Use authenticated user's ID
                            name: selectedFormula.name,
                            type: selectedFormula.type,
                            access: selectedFormula.access,
                            data: selectedFormula.data
                        }];
                        
                        // Extract field IDs from formula and add to projection
                        try {
                            const formulaData = JSON.parse(selectedFormula.data);
                            const formulaText = formulaData.formula || '';
                            const fieldMatches = formulaText.match(/FIELD_(\d+)/g);
                            if (fieldMatches) {
                                fieldMatches.forEach(match => {
                                    const fId = match.replace('FIELD_', '');
                                    if (!projectionFields.includes(fId)) {
                                        projectionFields.push(fId);
                                    }
                                });
                                projection.fields = projectionFields;
                            }
                        } catch (e) {
                            console.error('Error parsing formula data:', e);
                        }
                    }
                }
                let radius = typeof this.clusteringSettings.radius === 'string' 
                    ? parseFloat(this.clusteringSettings.radius) 
                    : this.clusteringSettings.radius;
                
                // Build settings based on field type
                const settings: any = {
                    filter: this.filter,
                    projection: projection,
                    radius: radius,
                    fieldId: null,
                    layerExposedEquationId: null,
                    formulaId: null,
                    datasetId: this.dataset.id,
                    limit: typeof this.clusteringSettings.limit === 'string' 
                        ? parseInt(this.clusteringSettings.limit, 10) 
                        : this.clusteringSettings.limit,
                    minCluster: this.clusteringSettings.minCluster || 0,
                    maxCluster: this.clusteringSettings.maxCluster || 0,
                    type: this.clusteringSettings.type
                };
                
                // Set the appropriate field based on type
                if (fieldType === 'field') {
                    settings.fieldId = this.clusteringSettings.fieldId;
                } else if (fieldType === 'layerExposeEquation') {
                    settings.layerExposedEquationId = parseInt(fieldId, 10);
                } else if (fieldType === 'formula') {
                    settings.formulaId = parseInt(fieldId, 10);
                    // fieldId is still required even when using formula
                    settings.fieldId = 'id';
                }
                
                this.dataPointsServiceState.emitOnApplySettings(settings);
            }));

        this.subscriptions.add(
            this.clusteringService
                .onResetClusterting()
                .subscribe(() => {
                    this.disableClustering()
                })
        );
    }

    selectCluster(index: number) {
        this.clusteringService.animateCluster(index);
    }

    openLocationProfile(datapoint: ClusterDatapoint) {
        this.dataPointsServiceState.emitOnDataPointSelecetd(datapoint);
    }

    sortDatapoints(sort: Sort) {
        const isAsc = sort.direction === 'asc';
        const fieldId = sort.active;
        let sortedDatapoints = this.selectedCluster.datapoints.sort((a, b) => {
            switch (fieldId) {
                case 'summary':
                    return a.summary.localeCompare(b.summary) * (isAsc ? 1 : -1);
                case 'clusteringField':
                case 'percentage':
                    return (a.value - b.value) * (isAsc ? 1 : -1);
            }
        });
        this.selectedCluster.datapoints = sortedDatapoints;
        this.table.renderRows();
    }

    enableClustering() {
        if (this.clusteringForm.valid) {
            this.clusterClone = undefined;
            this.selectedCluster = undefined;
            this.clusters = [];
            this.clusteringSettings = this.clusteringForm.value;
            
            // Save field type and ID to settings to persist across filter changes
            this.clusteringSettings.selectedFieldType = this.selectedFieldType;
            this.clusteringSettings.selectedFieldId = this.selectedFieldId;
            this.clusteringSettings.selectedFieldName = this.selectedFieldName;
            
            // Build projection fields - only include actual dataset fields, not equation/formula IDs
            const projectionFields: string[] = ['id', this.dataset.fields[this.dataset.mainSummaryFieldIndex].id];
            
            // Only add fieldId to projection if it's a regular numeric field
            if (this.selectedFieldType === 'field') {
                this.clusteringField = this.datapointsPageStateService.getActiveDatasetFields().get(this.clusteringSettings.fieldId);
                projectionFields.unshift(this.clusteringSettings.fieldId);
            } else if (this.selectedFieldType === 'layerExposeEquation') {
                // Set clusteringField for layer expose equation
                const selectedLayerExpose = this.layerExposeFields.find(f => f.id.toString() === this.selectedFieldId);
                if (selectedLayerExpose) {
                    this.clusteringField = {
                        id: selectedLayerExpose.id.toString(),
                        name: selectedLayerExpose.name,
                        displayName: selectedLayerExpose.name,
                        baseType: DatasetFieldType.NUMBER
                    } as DatasetField;
                }
            } else if (this.selectedFieldType === 'formula') {
                // Set clusteringField for formula
                const selectedFormula = this.formulas.find(f => f.id.toString() === this.selectedFieldId);
                if (selectedFormula) {
                    this.clusteringField = {
                        id: selectedFormula.id.toString(),
                        name: selectedFormula.name,
                        displayName: selectedFormula.name,
                        baseType: DatasetFieldType.NUMBER
                    } as DatasetField;
                }
            }
            
            const projection: any = {
                datasetID: this.dataset.id,
                fields: projectionFields,
                linkFields: [],
                links: [],
                geometryPrecision: 25
            };
            
            // Add formulas array if a formula is selected
            if (this.selectedFieldType === 'formula' && this.selectedFieldId) {
                const selectedFormula = this.formulas.find(f => f.id.toString() === this.selectedFieldId);
                if (selectedFormula) {
                    // Include all WorkspaceItem fields with current user's ID
                    projection.formulas = [{
                        id: selectedFormula.id,
                        accountId: selectedFormula.accountId,
                        datasetId: selectedFormula.datasetId,
                        userId: this.principal.id, // Use authenticated user's ID
                        name: selectedFormula.name,
                        type: selectedFormula.type,
                        access: selectedFormula.access,
                        data: selectedFormula.data
                    }];
                    
                    // Extract field IDs from formula and add to projection
                    try {
                        const formulaData = JSON.parse(selectedFormula.data);
                        const formulaText = formulaData.formula || '';
                        // Extract FIELD_X references
                        const fieldMatches = formulaText.match(/FIELD_(\d+)/g);
                        if (fieldMatches) {
                            fieldMatches.forEach(match => {
                                const fieldId = match.replace('FIELD_', '');
                                if (!projectionFields.includes(fieldId)) {
                                    projectionFields.push(fieldId);
                                }
                            });
                            // Update projection with new fields
                            projection.fields = projectionFields;
                        }
                    } catch (e) {
                        console.error('Error parsing formula data:', e);
                    }
                } else {
                    console.warn('Formula not found in this.formulas array!');
                }
            }
            
            let radius = typeof this.clusteringSettings.radius === 'string' 
                ? parseFloat(this.clusteringSettings.radius) 
                : this.clusteringSettings.radius;
            if (this.clusteringSettings.distanceUnit === DistanceUnit.MILES) {
                radius = GISUtils.milesToKm(radius);
            } else if (this.clusteringSettings.distanceUnit === DistanceUnit.FEET) {
                radius = GISUtils.milesToFoot(radius);
            } else if (this.clusteringSettings.distanceUnit === DistanceUnit.METER) {
                radius = GISUtils.milesToMeter(radius);
            }
            let clonedFilter = ObjectUtils.clone(this.filter);
            // if (this.principal.isSuperadmin) {
            //     clonedFilter.groups = [];
            // }
            // for normal users groups are mandatory
            
            // Build the clustering request based on field type
            const clusteringRequest: any = {
                filter: clonedFilter,
                projection: projection,
                radius: radius,
                datasetId: this.dataset.id,
                fieldId: null,
                layerExposedEquationId: null,
                formulaId: null,
                limit: typeof this.clusteringSettings.limit === 'string' 
                    ? parseInt(this.clusteringSettings.limit, 10) 
                    : this.clusteringSettings.limit,
                minCluster: this.clusteringSettings.minCluster || 0,
                maxCluster: this.clusteringSettings.maxCluster || 0,
                type: this.clusteringSettings.type,
                selectedFieldType: this.selectedFieldType,
                selectedFieldId: this.selectedFieldId,
                selectedFieldName: this.selectedFieldName
            };
            
            // Set the appropriate field based on type
            if (this.selectedFieldType === 'field') {
                clusteringRequest.fieldId = this.clusteringSettings.fieldId;
            } else if (this.selectedFieldType === 'layerExposeEquation') {
                clusteringRequest.layerExposedEquationId = parseInt(this.selectedFieldId, 10);
            } else if (this.selectedFieldType === 'formula') {
                clusteringRequest.formulaId = parseInt(this.selectedFieldId, 10);
                // fieldId is still required even when using formula
                clusteringRequest.fieldId = 'id';
            }
            
            this.clusteringService.enableClusteringMode(clusteringRequest);
        } else {
            this.notifService.error('Please recheck clustering settings');
        }
    }

    disableClustering() {
        this.clusterClone = null;
        this.selectedCluster = null;
        this.clusters = [];
        this.clusteringService.disableClustering();
        this.clusteringSettings = undefined;
        this.dataPointsServiceState.emitOnCancelClustering();
        
        // Clear field selection properties
        this.selectedFieldType = 'field';
        this.selectedFieldId = '';
        this.selectedFieldName = '';
        this.clusteringField = null;
        
        // Clear initSettings so form reinitializes with defaults
        this.initSettings = null;
        
        // Completely recreate the form with default values
        this.initClusteringForm();
        
        // Explicitly handle fieldId control to prevent red styling
        const fieldIdControl = this.clusteringForm.get('fieldId');
        if (fieldIdControl) {
            // Set to null and mark as untouched/pristine
            fieldIdControl.setValue(null, { emitEvent: false });
            fieldIdControl.markAsUntouched();
            fieldIdControl.markAsPristine();
            fieldIdControl.updateValueAndValidity({ emitEvent: false });
        }
        
        // Mark the entire form as untouched to prevent validation errors
        this.clusteringForm.markAsUntouched();
        this.clusteringForm.markAsPristine();
    }

    get DatasetFieldType() {
        return DatasetFieldType;
    }

    downloadVisibleClusters() {
        if (!this.clusters || this.clusters.length === 0) {
            this.notifService.error('No clusters to download');
            return;
        }

        // Prepare cluster summary data
        const clustersData = this.clusters.map((cluster, index) => ({
            index: index + 1,
            value: cluster.value || 0,
            count: cluster.datapointsCount || 0
        }));

        let radius = this.clusteringSettings.radius;

        // Clone filter to match what was used when generating clusters
        let clonedFilter = ObjectUtils.clone(this.filter);
       
        // Build projection fields - match what was used when generating clusters
        const projectionFields: string[] = ['id', this.dataset.fields[this.dataset.mainSummaryFieldIndex].id];
        if (this.clusteringSettings.selectedFieldType === 'field' && this.clusteringSettings.fieldId) {
            projectionFields.unshift(this.clusteringSettings.fieldId);
        }

        const projection: any = {
            datasetID: this.dataset.id,
            fields: projectionFields,
            linkFields: [],
            links: [],
            geometryPrecision: 25
        };

        // Add formulas array if a formula was used
        if (this.clusteringSettings.selectedFieldType === 'formula' && this.clusteringSettings.selectedFieldId) {
            const selectedFormula = this.formulas.find(f => f.id.toString() === this.clusteringSettings.selectedFieldId);
            if (selectedFormula) {
                projection.formulas = [{
                    id: selectedFormula.id,
                    accountId: selectedFormula.accountId,
                    datasetId: selectedFormula.datasetId,
                    userId: this.principal.id,
                    name: selectedFormula.name,
                    type: selectedFormula.type,
                    access: selectedFormula.access,
                    data: selectedFormula.data
                }];

                // Extract field IDs from formula
                try {
                    const formulaData = JSON.parse(selectedFormula.data);
                    const formulaText = formulaData.formula || '';
                    const fieldMatches = formulaText.match(/FIELD_(\d+)/g);
                    if (fieldMatches) {
                        fieldMatches.forEach(match => {
                            const fieldId = match.replace('FIELD_', '');
                            if (!projectionFields.includes(fieldId)) {
                                projectionFields.push(fieldId);
                            }
                        });
                        projection.fields = projectionFields;
                    }
                } catch (e) {
                    console.error('Error parsing formula data for download:', e);
                }
            }
        }

        // Build ClusteringRequest object that was used to generate clusters
        const clusteringRequest: ClusteringRequest = {
            datasetId: this.dataset.id,
            filter: clonedFilter,
            projection: projection,
            fieldId: this.clusteringSettings.fieldId,
            layerExposedEquationId: this.clusteringSettings.selectedFieldType === 'layerExposeEquation' 
                ? parseInt(this.clusteringSettings.selectedFieldId, 10) 
                : undefined,
            formulaId: this.clusteringSettings.selectedFieldType === 'formula' 
                ? parseInt(this.clusteringSettings.selectedFieldId, 10) 
                : undefined,
            radius: radius,
            limit: this.clusteringSettings.limit,
            minCluster: this.clusteringSettings.minCluster || 0,
            maxCluster: this.clusteringSettings.maxCluster || 0,
            type: this.clusteringSettings.type,
            selectedFieldType: this.clusteringSettings.selectedFieldType,
            selectedFieldId: this.clusteringSettings.selectedFieldId,
            selectedFieldName: this.clusteringSettings.selectedFieldName,
            distanceUnit: this.clusteringSettings.distanceUnit,
            openSidePanel: false
        };

        // Build request payload with clusteringSettings nested object
        const request = {
            clusteringSettings: clusteringRequest,
            clusters: clustersData,
            fieldName: this.clusteringField.name
        };

        const fileName = 'clusters_summary.xlsx';
        this.downloadService.downloadClustersSummary(this.dataset.id, request, fileName).subscribe(
            response => {
                AttachmentUtils.downloadFileWithName(response, fileName);
            }, 
            error => {
                this.notifService.error('Something went wrong during download');
            }
        );
    }

    downloadClusterContent(cluster, i) {
        let radius = this.clusteringSettings.radius;
        this.selectCluster(i);
        let datapointIds = this.selectedCluster.datapoints.map(dp => dp.id);
        let clonedFilter = ObjectUtils.clone(this.filter);
        
        // Build projection with all dataset fields
        const projectionFields = this.dataset.fields.map(f => f.id);
        const projection: any = {
            datasetID: this.dataset.id,
            geometryPrecision: 25,
            fields: projectionFields,
            linkFields: [],
            links: []
        };
        
        // Add formulas array if a formula is selected
        if (this.clusteringSettings.selectedFieldType === 'formula' && this.clusteringSettings.selectedFieldId) {
            const selectedFormula = this.formulas.find(f => f.id.toString() === this.clusteringSettings.selectedFieldId);
            if (selectedFormula) {
                projection.formulas = [{
                    id: selectedFormula.id,
                    accountId: selectedFormula.accountId,
                    datasetId: selectedFormula.datasetId,
                    userId: this.principal.id,
                    name: selectedFormula.name,
                    type: selectedFormula.type,
                    access: selectedFormula.access,
                    data: selectedFormula.data
                }];
                
                // Extract field IDs from formula and ensure they're in projection
                try {
                    const formulaData = JSON.parse(selectedFormula.data);
                    const formulaText = formulaData.formula || '';
                    const fieldMatches = formulaText.match(/FIELD_(\d+)/g);
                    if (fieldMatches) {
                        fieldMatches.forEach(match => {
                            const fieldId = match.replace('FIELD_', '');
                            if (!projectionFields.includes(fieldId)) {
                                projectionFields.push(fieldId);
                            }
                        });
                        projection.fields = projectionFields;
                    }
                } catch (e) {
                    console.error('Error parsing formula data for download:', e);
                }
            }
        }
        
        let request: DownloadDatapointRequest = {
            datasetId: this.dataset.id,
            filter: clonedFilter,
            timezone: Constants.DEFAULT_TIMEZONE,
            dateFormat: Constants.DEFAULT_DATE_FORMAT,
            outputFileType: DownloadFileType.XLSX,
            datapointIds: datapointIds,
            center: cluster.center,
            projection: projection,
            size: cluster.datapointsCount,
            fieldId: this.clusteringSettings.selectedFieldType === 'field' 
                ? this.clusteringSettings.fieldId 
                : (this.clusteringSettings.selectedFieldType === 'formula' ? 'id' : null),
            radius: radius,
            h3Indices: cluster.h3Indices,
            zoomLevel: cluster.zoomLevel,
            // reportRequest: {},
        };
        
        // Add layer expose equation fields if selected
        if (this.clusteringSettings.selectedFieldType === 'layerExposeEquation' && this.clusteringSettings.selectedFieldId) {
            request.aggregateByPolicy = true;
            request.layerExposedEquationId = parseInt(this.clusteringSettings.selectedFieldId, 10);
        }
        let fileName = 'cluster-content.xlsx';
        this.downloadService.downloadDatapoints(this.dataset.id, request, fileName).subscribe(
            response => {
                AttachmentUtils.downloadFileWithName(response, fileName);
            }, error => this.notifService.error('Something went wrong during download'));
    }

    get DatasetFieldScope() {
        return DatasetFieldScope;
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    // allowing only numbers
    @HostListener ('keydown', ['$event'])
    onKeydown(event: KeyboardEvent) {
        let regExp = new RegExp('\\d+');
        if (!event.key.match(regExp) && !this.validChars.includes(event.key)) {
            event.preventDefault();
        }
    }

   // custom validator / cannot allow starting the number with 0
   forbiddenZeroFirst(control: UntypedFormControl): {[key: string]: boolean} {
        if (control.value !== null) {
            if (control.value.toString().startsWith('0')) {return {zeroFirstIsForbidden : true};
            }
            if (control.value.toString().includes('.')) {
                return {zeroFirstIsForbidden : true};
            }
        }
        return null;
   }

   radiusValidator(control: UntypedFormControl): {[key: string]: boolean} {
       let valueString = control.value.toString();
       if (valueString[0] && valueString[1]) {
           if (valueString[0] === '0' && valueString[1] !== '.') {
               return {radiusValidator : true};
           }
       }
       if (valueString && valueString.startsWith('.')) {
           return {radiusValidator : true};
       }
       let counter = 0;
       for (let i=0; i < valueString.length; i++) {
           if (valueString[i] === '.') {
               counter++;
           }
       }
       if (counter > 1) {
           return {radiusValidator : true};
       }
       return  null;
   }

   distanceUnitCall($event) {
    this.clusteringForm.controls.radius.setValidators(null);
    if ($event === DistanceUnit.FEET || $event === DistanceUnit.METER) {
        this.clusteringForm.controls.radius.setValidators([Validators.required, Validators.max(this.MAXIMUM_RADIUS_FOR_FEET), Validators.maxLength(7), this.radiusValidator.bind(this)]);
    } else {
        this.clusteringForm.controls.radius.setValidators([Validators.required, Validators.max(this.MAXIMUM_RADIUS), Validators.maxLength(7), this.radiusValidator.bind(this)]);
    }
    this.clusteringForm.controls.radius.updateValueAndValidity();
   }

   // Field selection methods for 3 sections (Numeric Fields, Layer Expose Equations, Formulas)
   
   /**
    * Initialize eligible numeric fields from dataset
    */
   initEligibleNumericFields() {
       if (this.dataset && this.dataset.fields) {
           this.eligibleNumericFields = this.dataset.fields.filter(
               field => field.baseType === DatasetFieldType.NUMBER && 
                       field.scope === DatasetFieldScope.INTERNAL &&
                       !field.isGenerated &&
                       !field.tags?.includes('ID')
           );
       }
   }

   /**
    * Fetch layer expose fields from the service
    */
   fetchLayerExposeFields() {
       if (this.dataset && this.dataset.accountID) {
           this.layerExposeService.getAppliedLayerExpose(this.dataset.accountID).subscribe(
               (data) => {
                   if (Array.isArray(data) && data.length > 0) {
                       this.layerExposeFields = data.map((equation) => ({
                           id: equation.id,
                           accountId: equation.accountId,
                           name: equation.name,
                           baseValue: equation.baseValue,
                           layerValue: equation.layerValue,
                           attachmentPoint: equation.attachmentPoint,
                           isApplied: equation.isApplied,
                           participation: equation.participation,
                       }));
                   } else {
                       this.layerExposeFields = [];
                   }
                   this.cd.detectChanges();
               },
               (error) => {
                   console.error("Failed to fetch layer expose fields", error);
                   this.layerExposeFields = [];
               }
           );
       }
   }

   /**
    * Filter function for field search
    */
   filterFieldSearchFilter = (field: any): boolean => {
       if (!this.filterFieldSearchString) return true;
       const name = field.displayName || field.name || '';
       return name.toLowerCase().includes(this.filterFieldSearchString.toLowerCase());
   }

   /**
    * Handle field selection from numeric fields section
    */
   onSelectNumericField(field: DatasetField, event?: any) {
       // Only process if this is a user selection (not deselection)
       if (event && !event.isUserInput) {
           return;
       }
       
       if (event && event.source && !event.source.selected) {
           return;
       }
       
       this.selectedFieldType = 'field';
       this.selectedFieldId = field.id;
       this.selectedFieldName = field.displayName || field.name;
       this.clusteringForm.controls.fieldId.setValue(field.id);
       // Don't update clusteringField here - only when Apply is clicked
   }

   /**
    * Handle field selection from layer expose equations section
    */
   onSelectLayerExposeField(field: LayerExposeField, event?: any) {
       // Only process if this is a user selection (not deselection)
       if (event && !event.isUserInput) {
           return;
       }
       
       if (event && event.source && !event.source.selected) {
           return;
       }
       
       this.selectedFieldType = 'layerExposeEquation';
       this.selectedFieldId = field.id.toString();
       this.selectedFieldName = field.name;
       this.clusteringForm.controls.fieldId.setValue(field.id.toString());
       // Don't update clusteringField here - only when Apply is clicked
   }

   /**
    * Handle field selection from formulas section
    */
   onSelectFormula(formula: WorkspaceItem, event?: any) {
       // Only process if this is a user selection (not deselection)
       if (event && !event.isUserInput) {
           return;
       }
       
       if (event && event.source && !event.source.selected) {
           return;
       }
       
       this.selectedFieldType = 'formula';
       this.selectedFieldId = formula.id.toString();
       this.selectedFieldName = formula.name;
       this.clusteringForm.controls.fieldId.setValue(formula.id.toString());
       // Don't update clusteringField here - only when Apply is clicked
   }

   /**
    * Check if a numeric field is selected
    */
   isNumericFieldSelected(field: DatasetField): boolean {
       return this.selectedFieldType === 'field' && this.selectedFieldId === field.id;
   }

   /**
    * Check if a layer expose field is selected
    */
   isLayerExposeFieldSelected(field: LayerExposeField): boolean {
       return this.selectedFieldType === 'layerExposeEquation' && this.selectedFieldId === field.id.toString();
   }

   /**
    * Check if a formula is selected
    */
   isFormulaSelected(formula: WorkspaceItem): boolean {
       return this.selectedFieldType === 'formula' && this.selectedFieldId === formula.id.toString();
   }

   /**
    * Clear field search string
    */
   emptySearchString() {
       this.filterFieldSearchString = '';
   }

   /**
    * Get display placeholder for field dropdown
    */
   getFieldPlaceholder(): string {
       return 'Select Value';
   }

}
